import { Text } from '@mantine/core'
import L, { CRS, type LatLng, type LatLngBoundsExpression } from 'leaflet'
import { useEffect, useRef, useState } from 'react'
import { ImageOverlay, MapContainer, Popup, Rectangle, useMap, useMapEvents } from 'react-leaflet'
import type { OccupancyMapBerth } from '@/frontend/hooks/useOccupancy'
import { CurrencyText } from './CurrencyText'
import { berthStatusColor } from './colors'
import { StatusBadge } from './StatusBadge'

const CANVAS_WIDTH = 2000
const CANVAS_HEIGHT = 1400
const BOUNDS: LatLngBoundsExpression = [
  [0, 0],
  [CANVAS_HEIGHT, CANVAS_WIDTH],
]

const COLOR_HEX: Record<string, string> = {
  green: '#40C057',
  blue: '#339AF0',
  teal: '#12B886',
  yellow: '#FAB005',
  gray: '#868E96',
  red: '#FA5252',
  orange: '#FD7E14',
}

export type BerthPositionPatch = { id: string; mapX: number; mapY: number }

interface Props {
  berths: OccupancyMapBerth[]
  mapImageUrl?: string
  onBerthClick?: (berth: OccupancyMapBerth) => void
  height?: number | string
  editMode?: boolean
  onPositionChange?: (patch: BerthPositionPatch) => void
}

export function BerthMap({
  berths,
  mapImageUrl = '/marina/benoa-bali.svg',
  onBerthClick,
  height = 'calc(100vh - 220px)',
  editMode = false,
  onPositionChange,
}: Props) {
  // Local position overrides while dragging — keyed by berth id. Cleared when
  // the prop list refreshes (e.g. after save invalidates the query).
  const [overrides, setOverrides] = useState<Record<string, { mapX: number; mapY: number }>>({})
  // biome-ignore lint/correctness/useExhaustiveDependencies: clear overrides whenever new berth data arrives
  useEffect(() => {
    setOverrides({})
  }, [berths])

  return (
    <MapContainer
      crs={CRS.Simple}
      bounds={BOUNDS}
      maxBounds={L.latLngBounds([-200, -200], [CANVAS_HEIGHT + 200, CANVAS_WIDTH + 200])}
      minZoom={-2}
      maxZoom={2}
      zoomSnap={0.25}
      zoomDelta={0.5}
      style={{
        height,
        width: '100%',
        background: 'light-dark(#E3EDF3, var(--mantine-color-dark-8))',
      }}
      attributionControl={false}
    >
      <FitToBounds />
      <ImageOverlay url={mapImageUrl} bounds={BOUNDS} />
      {berths.map((b) => {
        const ovr = overrides[b.id]
        const mapX = ovr?.mapX ?? b.mapX
        const mapY = ovr?.mapY ?? b.mapY
        const hasPos =
          typeof mapX === 'number' &&
          typeof mapY === 'number' &&
          typeof b.mapWidth === 'number' &&
          typeof b.mapHeight === 'number'
        if (!hasPos) return null

        return (
          <DraggableBerthRect
            key={b.id}
            berth={b}
            mapX={mapX as number}
            mapY={mapY as number}
            editMode={editMode}
            onBerthClick={onBerthClick}
            onLocalMove={(x, y) => setOverrides((prev) => ({ ...prev, [b.id]: { mapX: x, mapY: y } }))}
            onCommit={(x, y) => onPositionChange?.({ id: b.id, mapX: x, mapY: y })}
          />
        )
      })}
    </MapContainer>
  )
}

interface DraggableBerthRectProps {
  berth: OccupancyMapBerth
  mapX: number
  mapY: number
  editMode: boolean
  onBerthClick?: (b: OccupancyMapBerth) => void
  onLocalMove: (x: number, y: number) => void
  onCommit: (x: number, y: number) => void
}

function DraggableBerthRect({
  berth,
  mapX,
  mapY,
  editMode,
  onBerthClick,
  onLocalMove,
  onCommit,
}: DraggableBerthRectProps) {
  const map = useMap()
  const w = berth.mapWidth ?? 0
  const h = berth.mapHeight ?? 0
  // CRS.Simple has Y inverted — top of screen = high lat.
  const bounds: LatLngBoundsExpression = [
    [CANVAS_HEIGHT - mapY - h, mapX],
    [CANVAS_HEIGHT - mapY, mapX + w],
  ]

  const statusColor = COLOR_HEX[berthStatusColor[berth.status]] ?? COLOR_HEX.gray
  const occupied = !!berth.currentBooking
  const fillColor = editMode ? COLOR_HEX.orange : occupied ? COLOR_HEX.blue : statusColor

  const dragState = useRef<{ originX: number; originY: number; startLatLng: LatLng } | null>(null)
  const movedRef = useRef(false)

  // Listen to map mousemove + mouseup at the parent map level so we keep tracking
  // even if the cursor briefly leaves the rectangle.
  useMapEvents({
    mousemove(e) {
      const ds = dragState.current
      if (!ds) return
      const dx = e.latlng.lng - ds.startLatLng.lng
      const dy = ds.startLatLng.lat - e.latlng.lat // inverted Y
      const newX = Math.max(0, Math.min(CANVAS_WIDTH - w, ds.originX + dx))
      const newY = Math.max(0, Math.min(CANVAS_HEIGHT - h, ds.originY + dy))
      onLocalMove(newX, newY)
      movedRef.current = true
    },
    mouseup() {
      const ds = dragState.current
      if (!ds) return
      dragState.current = null
      map.dragging.enable()
      if (movedRef.current) {
        // Commit final position after small async — read latest from overrides via callback chain.
        // We just emit current override (parent already has it via onLocalMove).
        onCommit(mapX, mapY)
      }
      movedRef.current = false
    },
  })

  return (
    <Rectangle
      bounds={bounds}
      pathOptions={{
        color: editMode ? COLOR_HEX.orange : statusColor,
        fillColor,
        fillOpacity: editMode ? 0.5 : 0.7,
        weight: editMode ? 2 : 1.5,
        dashArray: editMode ? '4,4' : undefined,
      }}
      eventHandlers={{
        mousedown: (e) => {
          if (!editMode) return
          dragState.current = { originX: mapX, originY: mapY, startLatLng: e.latlng }
          movedRef.current = false
          map.dragging.disable()
        },
        click: () => {
          // Only open popup/click handler if not editing OR didn't drag.
          if (!editMode) onBerthClick?.(berth)
        },
        mouseover: (e) => {
          if (!editMode) e.target.setStyle({ fillOpacity: 0.9 })
        },
        mouseout: (e) => {
          if (!editMode) e.target.setStyle({ fillOpacity: 0.7 })
        },
      }}
    >
      {!editMode && (
        <Popup>
          <BerthPopupContent berth={berth} />
        </Popup>
      )}
    </Rectangle>
  )
}

function FitToBounds() {
  const map = useMap()
  useEffect(() => {
    map.fitBounds(BOUNDS, { padding: [20, 20] })
  }, [map])
  return null
}

function BerthPopupContent({ berth }: { berth: OccupancyMapBerth }) {
  return (
    <div style={{ minWidth: 220 }}>
      <Text fw={700} size="sm" mb={4}>
        {berth.code}
      </Text>
      <Text size="xs" c="dimmed" mb={6}>
        {berth.pontoon?.name ?? `Pontoon ${berth.pontoon?.code ?? ''}`}
      </Text>
      <div style={{ marginBottom: 6 }}>
        <StatusBadge status={berth.status} kind="berth" />
      </div>
      <Text size="xs" c="dimmed">
        Max LOA: {Number(berth.maxLoaMeters).toFixed(1)} m · Beam: {Number(berth.maxBeamMeters).toFixed(1)} m · Depth:{' '}
        {Number(berth.depthMeters).toFixed(1)} m
      </Text>
      <Text size="xs" c="dimmed" mt={4}>
        Daily: <CurrencyText idr={berth.dailyRateIdr} span />
      </Text>
      {berth.currentBooking && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Text fw={600} size="xs">
            {berth.currentBooking.code}
          </Text>
          <Text size="xs" c="dimmed">
            {berth.currentBooking.customer.firstName} {berth.currentBooking.customer.lastName} ·{' '}
            {berth.currentBooking.boat.name}
          </Text>
          <div style={{ marginTop: 4 }}>
            <StatusBadge status={berth.currentBooking.status} kind="booking" upper />
          </div>
        </div>
      )}
    </div>
  )
}
